/**
 * Manual Asset Counts across projects (AST-126).
 *
 * One row per manually counted project (or storage yard), one column per
 * manual asset, a Total column and a Grand Total row. Pure, so MC-5..MC-11
 * are tested without rendering. The output has the same shape as
 * locationTable's ({ columns, groups, project, grandTotal }), so the Asset
 * Counts export (utils/assetCountsExport) works on it unchanged.
 *
 * MC-5  scope: manual and Both projects/yards for the customer; beacon out
 * MC-6  grouped under Projects and Storage Yards, A->Z by name
 * MC-7  a column per manual asset with a non-zero count anywhere, A->Z
 *       (natural); archived assets with counts keep their column
 * MC-8  each cell = that asset's NET count across the whole project -- the
 *       project-total rule (utils/locationCounts.entryProjectDelta): To or
 *       nothing adds, From only removes, From+To is a move and nets to 0
 * MC-9  Total = sum of the row
 * MC-10 Grand Total = sum of every column and of Total
 * MC-11 projects with no manual asset counts are left out
 *
 * Counts saved without a manual asset (before AST-118) are NOT shown: this
 * screen is "manual asset counts". Their subtype has no column here, so
 * including them would make the row Total disagree with its columns. Asset
 * Counts (per project) still shows those under their subtype.
 */
import { entryProjectDelta } from './locationCounts';

const natural = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export const isManualCounted = (p) => p?.trackingMode === 'manual' || p?.trackingMode === 'hybrid';
export const isYard = (p) => p?.type === 'yard';

export const GROUPS = [
  { key: 'projects', label: 'Projects' },
  { key: 'yards', label: 'Storage Yards' },
];

/**
 * @param projects     the customer's projects [{ id, name, type, trackingMode }]
 * @param entriesById  { [projectId]: entries[] }
 * @param manualAssets the customer's manual assets [{ id, name, archived }],
 *                     archived included (so their counts keep a column)
 */
export function manualAssetCountsTable(projects, entriesById, manualAssets) {
  const assetById = new Map((manualAssets || []).map((a) => [a.id, a]));

  // project id -> { assetId -> net }
  const perProject = new Map();
  const used = new Map(); // assetId -> any non-zero anywhere
  for (const p of (projects || []).filter(isManualCounted)) {
    const net = {};
    for (const e of entriesById?.[p.id] || []) {
      if (!e?.manualAssetId || !assetById.has(e.manualAssetId)) continue;
      const q = Number(e.quantity);
      if (!Number.isFinite(q) || q === 0) continue;
      const dir = entryProjectDelta(e);
      if (dir === 0) continue;
      net[e.manualAssetId] = (net[e.manualAssetId] || 0) + q * dir;
    }
    perProject.set(p.id, net);
    for (const [id, n] of Object.entries(net)) if (n !== 0) used.set(id, true);
  }

  // MC-7: columns for assets with a non-zero count somewhere, A->Z.
  const columns = [...used.keys()]
    .map((id) => assetById.get(id))
    .sort((a, b) => natural(a.name, b.name))
    .map((a) => ({ id: a.id, name: a.name, archived: !!a.archived }));

  const rowOf = (p) => {
    const net = perProject.get(p.id) || {};
    const cells = columns.map((c) => net[c.id] || 0);
    return { id: p.id, name: p.name, cells, total: cells.reduce((s, n) => s + n, 0) };
  };

  const groups = GROUPS.map(({ key, label }) => ({
    key,
    label,
    rows: (projects || [])
      .filter(isManualCounted)
      .filter((p) => (key === 'yards' ? isYard(p) : !isYard(p)))
      // MC-11: only projects with a manual asset count. A project whose
      // assets net to zero everywhere has nothing to show either.
      .filter((p) => Object.values(perProject.get(p.id) || {}).some((n) => n !== 0))
      .sort((a, b) => natural(a.name || '', b.name || ''))
      .map(rowOf),
  })).filter((g) => g.rows.length > 0);

  const all = groups.flatMap((g) => g.rows);
  const cells = columns.map((_, i) => all.reduce((s, r) => s + r.cells[i], 0));
  return {
    columns,
    groups,
    project: null, // no "project level" row on this screen
    grandTotal: { cells, total: cells.reduce((s, n) => s + n, 0) },
  };
}
